// src/components/page-content-v4/modules/PageList.tsx
import React from 'react';
import { Link,useNavigate } from 'react-router-dom';
import { Plus, Trash2, Edit, User, Calendar, FileText,ChevronLeft } from 'lucide-react';
import Pagination from './Pagination';
import { toast } from 'react-hot-toast';
import { usePageListContext } from '../contexts/PageListContext';
import { deletePageContent } from '@/services/pageService';
import { PageListProps } from '../types';

const PageList: React.FC<PageListProps> = ({ pages, pagination, setPagination, totalItems, refreshPages }) => {
  const { templateId } = usePageListContext();

  const headers = [
    { title: 'Status', align: 'left' },
    { title: 'Title', align: 'left' },
    { title: 'Last Updated By', align: 'left' },
    { title: 'Last Updated', align: 'left' },
    { title: 'Actions', align: 'right' },
  ];

  const totalPages = Math.ceil(totalItems / pagination.pageSize);

  const handleDelete = async (pageId: string) => {
    if (window.confirm('Are you sure you want to delete this page?')) {
      try {
        await deletePageContent(pageId);
        toast.success('Page deleted successfully');
        refreshPages();
      } catch (error) {
        console.error('Error deleting page:', error);
        toast.error('Failed to delete page. Please try again.');
      }
    }
  };
  const navigate = useNavigate();
  return (
    <div className="space-y-6 pt-10">
      <div className="flex justify-between items-start">
        <div className='flex items-start gap-4'>
          <button onClick={() => navigate(-1)} className="p-1 hover:bg-gray-100 rounded-md bg-white border">
            <ChevronLeft className="h-6 w-6" />
          </button>
          <div>
          <h1 className="text-lg sm:text-xl font-semibold text-gray-900">Pages</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage and organize your content pages
          </p>
          </div>
        </div>
        <Link
          to={`/page-content/${templateId}/new`}
          className="bg-primary-500 text-white py-2 px-4 rounded-md text-sm font-medium hover:bg-primary-600  transition-colors duration-200 flex items-center ease-in-out
            transform hover:scale-105"
        >
          <Plus className="w-5 h-5 mr-2" />
          Create New Page
        </Link>
      </div>

      <div className="bg-white rounded-xl p-4 sm:p-6 overflow-hidden">
        <div className="overflow-y-hidden overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-200">
                {headers.map(({ title, align }) => (
                  <th
                    key={title}
                    scope="col"
                    className={`px-4 py-3 text-${align} text-sm font-medium uppercase`}
                  >
                    {title}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {pages.map((page) => (
                <tr
                  key={page.pageId}
                  className={`${
                    page.isDraft ? 'bg-yellow-50/40' : 'bg-white'
                  } hover:bg-gray-50 transition-colors duration-150`}
                >
                  <td className="px-4 py-4 whitespace-nowrap">
                    {page.isDraft ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-yellow-100 text-yellow-800">
                        <FileText className="mr-1 h-3 w-3" />
                        Draft
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-green-100 text-green-800">
                        <div className="w-1.5 h-1.5 rounded-full bg-green-500 mr-1.5" />
                        Published
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-4">
                    <div className="text-sm font-medium text-gray-900">
                      {page.title || 'Untitled Draft'}
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center">
                      <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center">
                        <User size={14} className="text-gray-500" />
                      </div>
                      <span className="ml-2 text-sm text-gray-500">
                        {page.lastUpdatedBy || 'Unknown'}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center text-sm text-gray-500">
                      <Calendar size={14} className="mr-2 text-gray-400" />
                      {new Date(page.updatedAt || '').toLocaleString()}
                    </div>
                  </td>
                  <td className="px-4 py-4 text-right space-x-3">
                    <Link
                      to={`/page-content/${templateId}/${page.pageId}`}
                      className="inline-flex items-center px-3 py-1.5 border border-transparent 
                        text-xs font-medium rounded-lg text-blue-700 bg-blue-100 
                        hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 
                        focus:ring-blue-500 transition-all duration-200"
                    >
                      <Edit className="w-3.5 h-3.5 mr-1" />
                      Edit
                    </Link>
                    <button
                      onClick={() => handleDelete(page.pageId!)}
                      className="inline-flex items-center px-3 py-1.5 border border-transparent 
                        text-xs font-medium rounded-lg text-red-700 bg-red-100 
                        hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 
                        focus:ring-red-500 transition-all duration-200"
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1" />
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-4 border-t border-gray-200">
          <Pagination
            currentPage={pagination.page}
            totalPages={totalPages}
            onPageChange={(page) => setPagination({ ...pagination, page })}
          />
        </div>
      </div>
    </div>
  );
};

export default PageList;
